import React, {createContext, useCallback, useContext, useEffect, useMemo, useState} from "react";
import {useSocketListener} from "@unibuddy/sockets";
import {useErrorReporting} from "@unibuddy/error-reporting";
import {ApolloError, useApolloClient} from "@apollo/client";
import {useAnalytics} from "@unibuddy/tracking";

import {assistantWritingEndEvent, assistantWritingEvent} from "../../../sockets/Sockets";
import {useAssistantInitiationContext} from "./AssistantInitiationProvider";
import {
    AssistantConversationMessage,
    GetAssistantConversationDocument,
    GetAssistantConversationQuery,
    GetAssistantConversationQueryVariables,
    SenderRole,
    useSendAssistantMessageMutation,
} from "../../../types/generated";
import {useActivityChecker} from "../../../hooks/useActivityChecker";
import {useUniversityContext} from "../../../providers/UniversityProvider";
import {SocketMessage} from "../types";
import {addNewMessageToCache} from "../utils/cache/addNewMessageToCache";
import {updateMessageInCache} from "../utils/cache/updateMessageInCache";
import {updateMessagesCache} from "../utils/cache/updateMessagesCache";
import {assistantMessageCreate} from "../utils/assistantMessage";
import {usePostMessage} from "../../../hooks/usePostMessage";
import {getErrorString} from "../../../utils/getErrorString";
import {MESSAGE_CHARACTER_LIMIT} from "../utils/constants";
import {events} from "../../../analytics/productSpecification";

export enum ConversationState {
    USER_CAN_SEND_MESSAGE = "USER_CAN_SEND_MESSAGE",
    SENDING_USER_MESSAGE = "SENDING_USER_MESSAGE",
    ASSISTANT_PROCESSING_RESPONSE = "ASSISTANT_PROCESSING_RESPONSE",
    ASSISTANT_PROCESSING_TIMEOUT = "ASSISTANT_PROCESSING_TIMEOUT",
    ASSISTANT_IS_TYPING = "ASSISTANT_IS_TYPING",
}

type AssistantConversationContextType = {
    onSendMessage: (input: string) => Promise<void>;
    updateAssistantMessage: (
        messageId: string,
        message: Partial<AssistantConversationMessage>,
    ) => void;
    messages: AssistantConversationMessage[];
    error?: ApolloError;
    conversationState: ConversationState;
};

const AssistantConversationContext = createContext<AssistantConversationContextType>({
    onSendMessage: async () => {},
    messages: [],
    error: undefined,
    updateAssistantMessage: () => {},
    conversationState: ConversationState.USER_CAN_SEND_MESSAGE,
});

export function useAssistantConversationContext() {
    return useContext(AssistantConversationContext);
}

export function AssistantConversationProvider({children}: Readonly<{children: React.ReactNode}>) {
    const {reportError} = useErrorReporting();
    const {postMessage} = usePostMessage();
    const {trackEvent} = useAnalytics();
    const client = useApolloClient();
    const {universityId, universitySlug} = useUniversityContext();
    const [socketMessageBuffer, setSocketMessageBuffer] = useState<SocketMessage[]>([]);
    const {messages, error, conversationId, token} = useAssistantInitiationContext();
    const [sendAssistantMessage] = useSendAssistantMessageMutation();
    const [conversationState, setConversationState] = useState<ConversationState>(
        ConversationState.USER_CAN_SEND_MESSAGE,
    );

    /**
     * Resetting
     */
    const clearLocalStorageByIds = useCallback(
        (universityId: string, conversationId: string) => {
            const keysToRemove: string[] = [];

            for (let i = 0; i < localStorage.length; i++) {
                const key: string | null = localStorage.key(i);
                if (key && (key.includes(universityId) || key.includes(conversationId))) {
                    keysToRemove.push(key);
                }
            }

            keysToRemove.forEach((key) => {
                localStorage.removeItem(key);
            });

            postMessage("chatVisible=false");
        },
        [postMessage],
    );

    /**
     * Assistant's activity checker functions
     */
    const onTimeout = useCallback(() => {
        if (!conversationId) return;

        setConversationState(ConversationState.ASSISTANT_PROCESSING_TIMEOUT);
        addNewMessageToCache({
            client,
            universityId,
            conversationId,
            token,
            socketMessage: {
                id: Date.now().toString(),
                message: "Something went wrong. Please refresh and try again.",
                chunkIndex: 0,
                createdAt: new Date().toISOString(),
                universityId,
                sender: SenderRole.System,
            },
        });
    }, [client, conversationId, universityId, token]);

    const {logActivity, stopActivityCheck} = useActivityChecker({
        callback: onTimeout,
    });

    /**
     * Updating message functions
     */
    // Use effect to update assistant's response message
    useEffect(() => {
        if (!conversationId) return;
        if (socketMessageBuffer.length > 0) {
            const sortedBuffer = [...socketMessageBuffer].sort(
                (itemA, itemB) => itemA.chunkIndex - itemB.chunkIndex,
            );

            const newMessageText = sortedBuffer.map((item) => item.message).join("");

            const newMessage = {
                ...sortedBuffer[sortedBuffer.length - 1],
                message: newMessageText,
                metadata: undefined,
            };

            if (sortedBuffer[0].chunkIndex == 1) {
                const isMessageNew = addNewMessageToCache({
                    client,
                    universityId,
                    conversationId,
                    token,
                    socketMessage: newMessage,
                });
                if (!isMessageNew) {
                    updateMessageInCache({client, socketMessage: newMessage});
                } else {
                    setConversationState(ConversationState.ASSISTANT_IS_TYPING);
                }
            }
        }
    }, [client, conversationId, socketMessageBuffer, universityId, token]);

    const modifyMessageCache = useCallback(
        (
            conversationId: string,
            universityId: string,
            token: string | undefined,
            modifiedMessages: (
                messages: AssistantConversationMessage[],
            ) => AssistantConversationMessage[],
        ) => {
            const result = client.readQuery<
                GetAssistantConversationQuery,
                GetAssistantConversationQueryVariables
            >({
                query: GetAssistantConversationDocument,
                variables: {
                    id: conversationId,
                    universityId,
                    token,
                },
            });

            const data = updateMessagesCache(result, modifiedMessages);

            if (!data) {
                return;
            }

            client.writeQuery<
                GetAssistantConversationQuery,
                GetAssistantConversationQueryVariables
            >({
                query: GetAssistantConversationDocument,
                variables: {
                    id: conversationId,
                    universityId,
                    token,
                },
                data,
            });
        },
        [client],
    );

    const onSendMessage = useCallback(
        async (input: string) => {
            if (!conversationId) return;
            try {
                if (input.toLowerCase().trim() === "/clear") {
                    clearLocalStorageByIds(universityId, conversationId);
                    client.clearStore();
                    window.location.reload();
                    return;
                }

                if (input.length > MESSAGE_CHARACTER_LIMIT) {
                    throw new Error("Chat message is over character limit");
                }

                const addCurrentMessage = (messages: AssistantConversationMessage[]) =>
                    messages.concat(
                        assistantMessageCreate(
                            `optimistic-message-${new Date().toISOString()}`,
                            input,
                            SenderRole.User,
                            universityId,
                        ),
                    );
                modifyMessageCache(conversationId, universityId, token, addCurrentMessage);
                setConversationState(ConversationState.SENDING_USER_MESSAGE);
                setSocketMessageBuffer([]); // This clears any leftover buffer from previous message

                await sendAssistantMessage({
                    variables: {conversationId, input, universityId, token},
                    fetchPolicy: "no-cache",
                });
                logActivity("start");
                setConversationState(ConversationState.ASSISTANT_PROCESSING_RESPONSE);
            } catch (error: unknown) {
                console.error(error);
                reportError(new Error(`Error while sending message: ${getErrorString(error)}`));
                throw error;
            }
        },
        [
            conversationId,
            modifyMessageCache,
            universityId,
            token,
            sendAssistantMessage,
            logActivity,
            clearLocalStorageByIds,
            client,
            reportError,
        ],
    );

    const updateAssistantMessage = useCallback(
        (messageId: string, messageUpdate: Partial<AssistantConversationMessage>) => {
            if (!conversationId) {
                return;
            }

            client.cache.updateQuery<
                GetAssistantConversationQuery,
                GetAssistantConversationQueryVariables
            >(
                {
                    query: GetAssistantConversationDocument,
                    variables: {
                        id: conversationId,
                        universityId,
                        token,
                    },
                },
                (result) =>
                    updateMessagesCache(result, (messages) =>
                        messages.map((message) => {
                            if (message.id === messageId) {
                                return {
                                    ...message,
                                    ...messageUpdate,
                                };
                            }
                            return message;
                        }),
                    ),
            );
        },
        [universityId, conversationId, token, client.cache],
    );

    /**
     * Sockets related functions
     */
    const onAssistantWriting = useCallback(
        (message: SocketMessage) => {
            logActivity(message.id);
            try {
                setSocketMessageBuffer((prevState) => [...prevState, message]);
            } catch (error) {
                console.error("Error handling assistant write message");
                console.error(error);
                reportError(
                    new Error(`Error handling assistant write message: ${getErrorString(error)}`),
                );
            }
        },
        [reportError, logActivity],
    );

    const onAssistantWritingEnd = useCallback(
        (message: SocketMessage) => {
            stopActivityCheck();
            try {
                if (message?.metadata) {
                    trackEvent(events.ambassadorRecommended.name, {
                        product: "Assistant",
                        conversationId,
                        buddyType: message?.metadata?.staffProfile ? "Staff" : "Student",
                        universitySlug,
                    });
                }
                updateMessageInCache({
                    client,
                    socketMessage: {...message, metadata: message.metadata ?? undefined},
                });
                setSocketMessageBuffer([]);
                setConversationState(ConversationState.USER_CAN_SEND_MESSAGE);
            } catch (error) {
                console.error("Error handling assistant write end message");
                console.error(error);
                reportError(new Error("Error handling assistant write end message"));
            }
        },
        [stopActivityCheck, client, trackEvent, conversationId, universitySlug, reportError],
    );

    useSocketListener({
        event: assistantWritingEvent,
        channelId: conversationId ?? "",
        onEvent: onAssistantWriting,
    });

    useSocketListener({
        event: assistantWritingEndEvent,
        channelId: conversationId ?? "",
        onEvent: onAssistantWritingEnd,
    });

    /**
     * Value and return
     */
    const value = useMemo(
        () => ({
            onSendMessage,
            updateAssistantMessage,
            messages,
            error,
            conversationState,
        }),
        [conversationState, error, onSendMessage, updateAssistantMessage, messages],
    );

    return (
        <AssistantConversationContext.Provider value={value}>
            {children}
        </AssistantConversationContext.Provider>
    );
}
